import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

// Cache for image URLs to avoid repeated Supabase calls
// Using Map for better performance and includes timestamp for cache invalidation
const imageUrlCache = new Map<string, { url: string; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Storage access cache to avoid repeated failed checks
const storageAccessCache = new Map<string, { success: boolean; timestamp: number; message: string }>();
const STORAGE_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

/**
 * Robust storage access checker with retry logic and fallback mechanisms
 * @returns Promise resolving to access status and details
 */
export const checkStorageAccess = async (retryCount = 0): Promise<{
  success: boolean;
  message: string;
  canList: boolean;
  canRead: boolean;
  bucketExists: boolean;
}> => {
  const cacheKey = `storage-access-${retryCount}`;
  const cached = storageAccessCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < STORAGE_CACHE_DURATION) {
    logger.debug("Using cached storage access result");
    return { 
      success: cached.success, 
      message: cached.message,
      canList: false,
      canRead: true,
      bucketExists: cached.success
    };
  }

  try {
    logger.debug(`🔍 Checking storage access (attempt ${retryCount + 1}/3)...`);
    
    // Step 1: Check authentication state
    const { data: { user } } = await supabase.auth.getUser();
    const isAuthenticated = !!user;
    logger.debug(`Authentication state: ${isAuthenticated ? 'authenticated' : 'anonymous'}`);

    // Step 2: Test bucket existence via list operation
    let canList = false;
    let bucketExists = false;
    
    try {
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();
      
      if (!listError && buckets) {
        const productBucket = buckets.find(bucket => bucket.name === 'product-images');
        bucketExists = !!productBucket;
        logger.debug(`Bucket exists: ${bucketExists}`);
        
        if (bucketExists) {
          // Test file listing capability
          const { error: filesError } = await supabase.storage
            .from('product-images')
            .list('', { limit: 1 });
          
          canList = !filesError;
          if (filesError) {
            logger.debug("List operation failed, but bucket exists:", filesError.message);
          }
        }
      } else {
        logger.debug("Bucket list failed:", listError?.message);
      }
    } catch (listException) {
      logger.debug("Exception during list operation:", listException);
    }

    // Step 3: Test read access via direct URL
    let canRead = false;
    try {
      // Test access to a known endpoint
      const testUrl = `https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/test.jpg`;
      const response = await fetch(testUrl, { 
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      // 404 means bucket is accessible but file doesn't exist (normal)
      // 200 means file exists and is accessible
      // Other codes might indicate issues
      canRead = response.status === 404 || response.status === 200;
      logger.debug(`Direct access test: ${response.status} (${canRead ? 'accessible' : 'not accessible'})`);
    } catch (readException) {
      logger.debug("Direct access test failed:", readException);
    }

    // Step 4: Determine overall success
    const success = bucketExists && (canList || canRead);
    let message = '';
    
    if (success) {
      if (canList && canRead) {
        message = "Storage is fully accessible";
      } else if (canRead) {
        message = "Storage is accessible via direct URLs (list operations restricted)";
      } else if (canList) {
        message = "Storage listing works but direct access may be limited";
      } else {
        message = "Storage bucket exists but access patterns are limited";
      }
    } else if (bucketExists) {
      message = "Storage bucket exists but access is restricted";
    } else {
      message = "Storage bucket not found or not accessible";
    }

    // Cache the result
    storageAccessCache.set(cacheKey, {
      success,
      message,
      timestamp: Date.now()
    });

    logger.debug(`Storage access check result: ${success ? 'SUCCESS' : 'FAILED'} - ${message}`);
    
    return {
      success,
      message,
      canList,
      canRead,
      bucketExists
    };

  } catch (error) {
    logger.error("Storage access check failed:", error);
    
    // Retry logic for transient issues
    if (retryCount < 2) {
      logger.debug("Retrying storage access check...");
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return checkStorageAccess(retryCount + 1);
    }
    
    const message = `Storage access check failed after ${retryCount + 1} attempts: ${error instanceof Error ? error.message : 'Unknown error'}`;
    
    // Cache the failure
    storageAccessCache.set(cacheKey, {
      success: false,
      message,
      timestamp: Date.now()
    });
    
    return {
      success: false,
      message,
      canList: false,
      canRead: false,
      bucketExists: false
    };
  }
};

/**
 * Ensures storage bucket exists - checks if it exists
 * @returns Promise resolving to true if bucket exists
 */
export const ensureStorageBucketExists = async (): Promise<boolean> => {
  try {
    logger.debug("🔍 Checking if product-images bucket exists...");
    
    const accessResult = await checkStorageAccess();
    return accessResult.bucketExists;
  } catch (error) {
    logger.warn('Exception during bucket check:', error);
    return false;
  }
};

/**
 * Resolves any image URL to a direct Supabase storage URL
 * Handles legacy image paths, full URLs, and relative paths
 */
export const getProductImageUrl = (imageUrl: string | null): string => {
  if (!imageUrl || imageUrl === "") {
    return "/placeholder.svg";
  }
  
  // If it's a placeholder, return it directly
  if (imageUrl === "/placeholder.svg") {
    return imageUrl;
  }
  
  // Check cache first
  const cachedEntry = imageUrlCache.get(imageUrl);
  if (cachedEntry && (Date.now() - cachedEntry.timestamp) < CACHE_DURATION) {
    if (process.env.NODE_ENV === 'development') {
      logger.debug("🟢 Cache HIT for:", imageUrl);
    }
    return cachedEntry.url;
  }
  
  let finalUrl: string;
  
  // Handle different URL formats
  if (imageUrl.startsWith('http')) {
    // If it's already a direct Supabase URL, return as-is
    if (imageUrl.includes('supabase.co/storage/v1/object/public/product-images/')) {
      finalUrl = imageUrl;
    }
    // For other external URLs (lovable-uploads, etc.), return as-is
    else {
      finalUrl = imageUrl;
    }
  }
  // Handle /photo/ proxy URLs by extracting filename and converting to direct URL
  else if (imageUrl.startsWith('/photo/')) {
    const rawPath = imageUrl.replace('/photo/', '');
    const encodedPath = rawPath.split('/').map(encodeURIComponent).join('/');
    finalUrl = `https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/${encodedPath}`;
    if (process.env.NODE_ENV === 'development') {
      logger.debug("🔄 Converting /photo/ URL to direct:", imageUrl, "->", finalUrl);
    }
  }
  // Handle site assets with leading slash
  else if (imageUrl.startsWith('/')) {
    // For lovable-uploads and other public assets, return as-is
    if (imageUrl.startsWith('/lovable-uploads/')) {
      finalUrl = imageUrl;
    } else {
      finalUrl = imageUrl;
    }
  }
  // Handle lovable-uploads without leading slash
  else if (imageUrl.startsWith('lovable-uploads/')) {
    finalUrl = `/${imageUrl}`;
  }
  // Handle bare filenames and product-images paths - convert to direct Supabase URLs
  else {
    // Normalize the path for consistent handling
    let finalPath = imageUrl.replace(/^\/+/, '');
    if (finalPath.startsWith('product-images/')) {
      finalPath = finalPath.replace('product-images/', '');
    }
    const encodedPath = finalPath.split('/').map(encodeURIComponent).join('/');
    
    finalUrl = `https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/${encodedPath}`;
    if (process.env.NODE_ENV === 'development') {
      logger.debug("🔄 Converting filename to direct URL:", imageUrl, "->", finalUrl);
    }
  }
  
  // Cache the result
  imageUrlCache.set(imageUrl, { url: finalUrl, timestamp: Date.now() });
  
  return finalUrl;
};

// Clear image URL from cache - useful after uploads
export const clearImageUrlCache = (imageUrl?: string): void => {
  if (imageUrl) {
    // Create array of all possible cache keys for this image
    const keysToDelete = [imageUrl];
    
    // If it's a direct Supabase URL, add related versions
    if (imageUrl.includes('supabase.co/storage/v1/object/public/product-images/')) {
      const pathMatch = imageUrl.match(/\/product-images\/(.+)$/);
      if (pathMatch) {
        const imagePath = pathMatch[1];
        keysToDelete.push(`/photo/${imagePath}`); // old proxy format
        keysToDelete.push(imagePath); // normalized path
      }
    }
    
    // If it's an old proxy URL, add the direct URL and normalized path
    if (imageUrl.startsWith('/photo/')) {
      const imagePath = imageUrl.replace('/photo/', '');
      keysToDelete.push(imagePath);
      keysToDelete.push(`https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/${imagePath}`);
    }
    
    // If it's a normalized path, add direct URL
    if (!imageUrl.startsWith('http') && !imageUrl.startsWith('/') && imageUrl !== "placeholder.svg") {
      keysToDelete.push(`https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/${imageUrl}`);
    }
    
    // Delete all possible cache keys
    keysToDelete.forEach(key => {
      if (imageUrlCache.has(key)) {
        imageUrlCache.delete(key);
        logger.debug("Cleared cache for key:", key);
      }
    });
    
  } else {
    // Clear entire cache if no specific URL is provided
    const cacheSize = imageUrlCache.size;
    imageUrlCache.clear();
    logger.debug(`Cleared entire image URL cache (${cacheSize} entries)`);
  }
};

// Clean up expired cache entries
const cleanupExpiredCache = (): void => {
  const now = Date.now();
  let cleanedCount = 0;
  
  for (const [key, value] of imageUrlCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      imageUrlCache.delete(key);
      cleanedCount++;
    }
  }
  
  if (cleanedCount > 0) {
    logger.debug(`Cleaned up ${cleanedCount} expired cache entries`);
  }
};

// Run cleanup every 10 minutes for better cache management
setInterval(cleanupExpiredCache, 10 * 60 * 1000);

// Only clean up expired entries on module load, don't clear everything
cleanupExpiredCache();

// Clean up expired entries when page becomes visible (don't clear everything)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Only clean up expired entries, not the entire cache
      cleanupExpiredCache();
    }
  });
}

/**
 * Extract product images with consistent format from any product object
 * Handles both legacy image_url and new product_images array
 */
export const getProductImages = (product: any) => {
  if (!product) return [];
  
  try {
    // If product has product_images array, use it
    if (product.product_images && Array.isArray(product.product_images) && product.product_images.length > 0) {
      // Sort by display_order if available
      const sortedImages = [...product.product_images]
        .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        
      return sortedImages.map(img => ({
        ...img,
        image_url: getProductImageUrl(img.image_url) // Ensure URLs are fully formed
      }));
    }
    
    // Otherwise, create a single image entry from the image_url if it exists
    if (product.image_url) {
      return [{ 
        id: 'legacy-image', 
        image_url: getProductImageUrl(product.image_url), 
        is_primary: true,
        display_order: 0
      }];
    }
    
    // Return empty array if no images
    return [];
  } catch (error) {
    logger.warn("Error processing product images:", error, "for product:", product?.id);
    return [];
  }
};

/**
 * Gets the primary image URL for a product with enhanced fallback
 * Handles both legacy image_url and new product_images array
 */
export const getPrimaryImageUrl = (product: any): string => {
  try {
    // Basic validation
    if (!product) return "/placeholder.svg";
    
    // Get all images using the helper function
    const images = getProductImages(product);
    
    if (!images || images.length === 0) return "/placeholder.svg";
    
    // Find primary image
    const primaryImage = images.find(img => img.is_primary);
    
    // Return primary image URL or first image URL
    const selectedImage = primaryImage || images[0];
    const imageUrl = selectedImage.image_url;
    
    // Additional validation - ensure we don't return undefined or invalid URLs
    if (!imageUrl || imageUrl === 'undefined' || imageUrl === 'null') {
      logger.warn("Invalid primary image URL for product:", product?.id, "got:", imageUrl);
      return "/placeholder.svg";
    }
    
    return imageUrl;
  } catch (error) {
    logger.warn("Error getting primary image:", error, "for product:", product?.id);
    return "/placeholder.svg";
  }
};

// Simplified version when you have already extracted the images array
export const getPrimaryImageFromArray = (images: Array<{ image_url: string, is_primary: boolean }> | null): string => {
  if (!images || images.length === 0) return "/placeholder.svg";
  
  try {
    // Find primary image
    const primaryImage = images.find(img => img.is_primary);
    
    // Return primary image URL or first image URL
    const imageUrl = primaryImage ? primaryImage.image_url : images[0].image_url;
    
    // Ensure the URL is fully formed
    return getProductImageUrl(imageUrl);
  } catch (error) {
    logger.error("Error getting primary image from array:", error);
    return "/placeholder.svg";
  }
};

// Preload high-priority images to improve LCP
export const preloadCriticalImages = (imageUrls: string[]) => {
  imageUrls.forEach(url => {
    if (!url || url === "/placeholder.svg") return;
    
    // Process URL through our cache system first
    const processedUrl = getProductImageUrl(url);
    
    const img = new Image();
    img.src = processedUrl;
    (img as any).fetchpriority = "high";
    img.decoding = "async";
    img.crossOrigin = "anonymous";
    
    // Cache the preloaded image
    img.onload = () => {
      if (process.env.NODE_ENV === 'development') {
        logger.debug("✅ Preloaded critical image:", processedUrl);
      }
    };
    
    img.onerror = () => {
      logger.warn("❌ Failed to preload critical image:", processedUrl);
      // Clear failed image from cache
      clearImageUrlCache(url);
    };
  });
};

// Enhanced cache status checker
export const getCacheStatus = () => {
  const now = Date.now();
  const totalEntries = imageUrlCache.size;
  let expiredEntries = 0;
  let validEntries = 0;
  
  for (const [key, value] of imageUrlCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      expiredEntries++;
    } else {
      validEntries++;
    }
  }
  
  return {
    totalEntries,
    validEntries,
    expiredEntries,
    cacheHitRate: validEntries / totalEntries || 0
  };
};

// Helper to generate image srcset for responsive images
export const generateSrcSet = (imageUrl: string): string => {
  if (!imageUrl || imageUrl === "/placeholder.svg") return imageUrl;
  
  // For this implementation, we're assuming Supabase doesn't have
  // automatic image resizing. In a real implementation with a CDN
  // that supports image resizing, you'd generate multiple sizes.
  return imageUrl;
};

/**
 * Enhanced debug utility to check storage status with comprehensive diagnostics
 * Can be called from components to diagnose image issues
 */
export const debugStorageStatus = async () => {
  try {
    logger.debug("🔍 Running comprehensive storage diagnostics...");
    
    // Use the robust storage access checker
    const accessResult = await checkStorageAccess();
    
    if (!accessResult.success) {
      return {
        success: false,
        message: accessResult.message,
        diagnostics: {
          bucketExists: accessResult.bucketExists,
          canList: accessResult.canList,
          canRead: accessResult.canRead
        }
      };
    }
    
    // If basic access works, run additional diagnostics
    const diagnostics = {
      bucketExists: accessResult.bucketExists,
      canList: accessResult.canList,
      canRead: accessResult.canRead,
      fileCount: 0,
      sampleFiles: [] as string[],
      publicUrlTest: null as boolean | null
    };
    
    // Try to get file listing if we can list
    if (accessResult.canList) {
      try {
        const { data: files, error: filesError } = await supabase.storage
          .from('product-images')
          .list('', { limit: 5 });
          
        if (!filesError && files) {
          diagnostics.fileCount = files.length;
          diagnostics.sampleFiles = files.map(f => f.name);
          
          // Test public URL if we have files
          if (files.length > 0) {
            const testFile = files[0];
            const { data } = supabase.storage
              .from('product-images')
              .getPublicUrl(testFile.name);
              
            try {
              const response = await fetch(data?.publicUrl || '', { method: 'HEAD' });
              diagnostics.publicUrlTest = response.status === 200;
              logger.debug(`Public URL test result: ${diagnostics.publicUrlTest}`);
            } catch (fetchError) {
              logger.debug("Public URL test failed:", fetchError);
              diagnostics.publicUrlTest = false;
            }
          }
        }
      } catch (listError) {
        logger.debug("File listing failed during diagnostics:", listError);
      }
    }
    
    // Test direct URL access pattern
    let directAccessWorking = false;
    if (accessResult.canRead) {
      try {
        const testUrl = `https://opnawbebzrjsaeoeqgkd.supabase.co/storage/v1/object/public/product-images/test.jpg`;
        const response = await fetch(testUrl, { method: 'HEAD' });
        directAccessWorking = response.status === 404 || response.status === 200;
      } catch (directError) {
        logger.debug("Direct access test failed:", directError);
      }
    }
    
    let message = accessResult.message;
    if (diagnostics.fileCount > 0) {
      message += ` (${diagnostics.fileCount} files found)`;
    }
    if (diagnostics.publicUrlTest === true) {
      message += " - Public URLs working";
    } else if (diagnostics.publicUrlTest === false) {
      message += " - Public URL access may be limited";
    }
    
    return {
      success: true,
      message,
      diagnostics,
      directAccessWorking,
      recommendations: generateStorageRecommendations(diagnostics, directAccessWorking)
    };
    
  } catch (error) {
    logger.error("❌ Storage diagnostics failed:", error);
    return {
      success: false,
      error,
      message: `Storage diagnostics failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      recommendations: [
        "Check network connectivity",
        "Verify Supabase project configuration",
        "Ensure storage bucket exists and is properly configured"
      ]
    };
  }
};

/**
 * Generate actionable recommendations based on storage diagnostics
 */
const generateStorageRecommendations = (diagnostics: any, directAccessWorking: boolean): string[] => {
  const recommendations: string[] = [];
  
  if (!diagnostics.bucketExists) {
    recommendations.push("Create the 'product-images' storage bucket");
    recommendations.push("Ensure bucket is set to public if images should be publicly accessible");
  }
  
  if (!diagnostics.canList && !directAccessWorking) {
    recommendations.push("Check Row Level Security (RLS) policies on storage.objects");
    recommendations.push("Verify user authentication state");
  }
  
  if (diagnostics.canList && diagnostics.fileCount === 0) {
    recommendations.push("Upload some test images to verify the upload process");
  }
  
  if (diagnostics.publicUrlTest === false) {
    recommendations.push("Check CORS settings for the storage bucket");
    recommendations.push("Verify bucket public access settings");
  }
  
  if (!diagnostics.canRead && !directAccessWorking) {
    recommendations.push("Test image URLs in a new browser tab to isolate CORS issues");
    recommendations.push("Check if images are accessible when logged in as admin");
  }
  
  if (recommendations.length === 0) {
    recommendations.push("Storage appears to be working correctly");
    recommendations.push("If you're still experiencing issues, try clearing the image cache");
  }
  
  return recommendations;
};
